import "./ModalChangeEnvironment.css";
import moment from "moment";
import { useEffect, useState } from "react";
import { closeUpdateEnvironment, getUser, setOwnerUserId, useDatabase, useGlobal, useHub, useOwnerUserId } from "../../core/Global";
import Modal from "../base/Modal";
import { API_ROUTES } from "../../core/Constants";
import Avatar from "../base/Avatar";
import User from "../../modules/accounts/models/User";
import Indicator from "../base/Indicator";
import Icon from "../base/Icon";

type SortField = "workspace" | "since" | "amount" | "status";
type SortDir = "asc" | "desc";
type SortState = { field: SortField; dir: SortDir } | null;


type StatusBadge = {
    label: string
    className: string
}

type SubState = "suspended" | "canceled" | "paymentIssue" | "onHold" | "active" | "expired" | "none";

// There is no trial plan — an account that is neither active, canceled nor suspended has simply
// expired (or never subscribed). This used to fall through to a "Trial" label, which hid real
// payment problems behind a status the product doesn't have.
const resolveState = (u: User): SubState => {
    if (u.suspended) return "suspended";

    // A new plan decides the status on its own; the old subscription is history for them.
    const plan = u.workspaceBilling?.kind === "plan" ? u.workspaceBilling : undefined;
    if (plan) {
        if (plan.status === "Canceled") return "canceled";
        if (plan.status === "Suspended") return "paymentIssue";
        if (plan.status === "OnHold") return "onHold";
        return "active";
    }

    const sub = u.subscription;
    if (!sub) return "none";

    if (sub.isCanceled) return "canceled";
    if (sub.isSuspended) return "paymentIssue";

    // An active period that is already past its end date is an unpaid renewal, not a live sub.
    if (sub.isActive) return sub.endsAt && new Date(sub.endsAt) < new Date() ? "paymentIssue" : "active";

    return "expired";
}

const STATUS_BADGES: Record<SubState, StatusBadge> = {
    suspended: { label: "Suspended", className: "badge-light-danger" },
    canceled: { label: "Canceled", className: "badge-light-danger" },
    paymentIssue: { label: "Payment Issue", className: "badge-light-warning" },
    onHold: { label: "On Hold", className: "badge-light-info" },
    active: { label: "Active", className: "badge-light-success" },
    expired: { label: "Expired", className: "badge-light-danger" },
    none: { label: "No Subscription", className: "badge-light" },
}

const STATUS_RANKS: Record<SubState, number> = {
    suspended: 0,
    canceled: 1,
    paymentIssue: 2,
    onHold: 3,
    expired: 3,
    none: 3,
    active: 4,
}

const resolveStatus = (u: User): StatusBadge => STATUS_BADGES[resolveState(u)];

// What the account really pays a month (plan, annual as a twelfth, plus paid add-ons). Older
// servers send no workspaceBilling, so fall back to the old subscription figure.
const monthlyOf = (u: User): number | undefined =>
    u.workspaceBilling ? u.workspaceBilling.monthlyTotal : (u.customSubscriptionCharge ?? u.subscription?.amount);

const renewsAtOf = (u: User): { date?: Date | string, active: boolean } => {
    const wb = u.workspaceBilling
    if (wb?.kind === "plan") return { date: wb.renewsAt, active: wb.status !== "Canceled" }
    return { date: u.subscription?.endsAt, active: !!u.subscription?.isActive }
}


const formatShortDate = (dt?: Date) => {
    if (!dt) return "-";
    const offset = getUser()?.timeZoneOffset ?? 0;
    return moment(dt).utc().add(offset, "minutes").format("DD MMM YYYY");
}

export default function ModalChangeEnvironment() {

    const hub = useHub()
    const db = useDatabase()
    const [globalState] = useGlobal()
    const currentOwnerId = useOwnerUserId()

    const [keyword, keywordSet] = useState<string>("")
    const [model, modelSet] = useState<User[]>([])
    const [loading, loadingSet] = useState<boolean>(true)
    const [switchingId, switchingIdSet] = useState<string>()
    const [sort, sortSet] = useState<SortState>(null)

    const cycleSort = (field: SortField) => {
        if (!sort || sort.field !== field) {
            sortSet({ field, dir: "asc" });
        }
        else if (sort.dir === "asc") {
            sortSet({ field, dir: "desc" });
        }
        else {
            sortSet(null);
        }
    }

    const sortIcon = (field: SortField) => {
        if (!sort || sort.field !== field) return "sort";
        return sort.dir === "asc" ? "arrow-up" : "arrow-down";
    }

    const statusRank = (u: User): number => STATUS_RANKS[resolveState(u)];

    // The list is fetched ONCE (every account, active or not) and searched here. Without a
    // keyword only accounts with a live subscription are offered, as before; a keyword searches
    // all of them. Order mirrors what the server used to return: the accounts this admin
    // switches to most come first.
    const isLive = (u: User) => {
        const st = resolveState(u)
        // A suspended new plan is still a running account the admin may need to fix.
        return st === "active" || st === "onHold" || (st === "paymentIssue" && u.workspaceBilling?.kind === "plan")
    }

    const filteredModel = (() => {
        // "@name" is how usernames are shown, so it is how people type them too.
        const kw = keyword.trim().toLowerCase().replace(/^@/, "")
        const has = (v?: string) => !!v && v.toLowerCase().includes(kw)
        const list = kw
            ? model.filter(u => has(u.fullName) || has(u.firstName) || has(u.lastName) || has(u.companyName) || has(u.email)
                || has(u.username) || !!u.memberUsernames?.some(has) || has(u.workspaceBilling?.planName))
            : model.filter(isLive)

        return [...list].sort((a, b) => {
            const sc = (b.switchCount ?? 0) - (a.switchCount ?? 0)
            if (sc !== 0) return sc
            if (kw) {
                const live = (isLive(b) ? 1 : 0) - (isLive(a) ? 1 : 0)
                if (live !== 0) return live
            }
            return (b.createdAt ? +new Date(b.createdAt) : 0) - (a.createdAt ? +new Date(a.createdAt) : 0)
        })
    })()

    const sortedModel = (() => {
        if (!sort) return filteredModel;
        const copy = [...filteredModel];
        const mul = sort.dir === "asc" ? 1 : -1;

        copy.sort((a, b) => {
            switch (sort.field) {
                case "workspace": {
                    const an = (a.fullName ?? "").toLowerCase();
                    const bn = (b.fullName ?? "").toLowerCase();
                    return an.localeCompare(bn) * mul;
                }
                case "since": {
                    const av = a.createdAt ? +new Date(a.createdAt) : 0;
                    const bv = b.createdAt ? +new Date(b.createdAt) : 0;
                    return (av - bv) * mul;
                }
                case "amount": {
                    const av = monthlyOf(a) ?? 0;
                    const bv = monthlyOf(b) ?? 0;
                    return (av - bv) * mul;
                }
                case "status": {
                    return (statusRank(a) - statusRank(b)) * mul;
                }
            }
        });

        return copy;
    })();

    useEffect(() => {
        loadingSet(true)
        hub.Get(API_ROUTES.Users + "envs?all=true", res => {
            modelSet(res.data ?? [])
            loadingSet(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const switchTo = (item: User) => {
        if (!item.id) return
        switchingIdSet(item.id)
        db.deleteDatabase().then(() => {
            hub.Post(API_ROUTES.Users + "envs", { id: item.id }, res => {
                if (res.success) {
                    setOwnerUserId(item.id!)
                    window.location.reload()
                }
                else {
                    switchingIdSet(undefined)
                }
            })
        })
    }


    return (
        <Modal id="modal-change-environment"
            title="Workspaces"
            onClose={closeUpdateEnvironment}
            open visible
            size="Large"
            noFooter
            onOpen={() => {
                $("#txt-workspaces-keyword").focus();
            }}>
            <div>
                <div className="mb-3 d-flex align-items-center gap-3">
                    <input id="txt-workspaces-keyword" type="text" autoComplete="one-time-code"
                        className="form-control form-control-sm form-control-solid flex-grow-1" placeholder="Search workspaces..."
                        value={keyword}
                        onChange={e => keywordSet(e.target.value)} />
                    <div className="fs-8 text-gray-600 fw-bold text-nowrap">
                        {
                            loading ? "..." : `${filteredModel.length} ${filteredModel.length === 1 ? "workspace" : "workspaces"}`
                        }
                    </div>
                </div>

                <div className="workspaces-table-wrapper">
                    <div className="workspaces-table-header">
                        <div className="ws-col-workspace ws-sortable" onClick={() => cycleSort("workspace")}>
                            WORKSPACE <Icon icon={sortIcon("workspace")} className="ms-1" size={10} />
                        </div>
                        <div className="ws-col-since ws-sortable" onClick={() => cycleSort("since")}>
                            MEMBER SINCE <Icon icon={sortIcon("since")} className="ms-1" size={10} />
                        </div>
                        <div className="ws-col-amount ws-sortable" onClick={() => cycleSort("amount")}>
                            PLAN / MONTHLY <Icon icon={sortIcon("amount")} className="ms-1" size={10} />
                        </div>
                        <div className="ws-col-status ws-sortable" onClick={() => cycleSort("status")}>
                            STATUS <Icon icon={sortIcon("status")} className="ms-1" size={10} />
                        </div>
                        <div className="ws-col-action"></div>
                    </div>

                    <div className="workspaces-table-body scroll h-350px h-xxl-500px">
                        {
                            loading ?
                                <div className="text-center py-10">
                                    <Indicator text="Loading..." />
                                </div>
                                : sortedModel.length === 0 ?
                                    <div className="text-center text-muted py-10 fs-7">
                                        No workspaces found.
                                    </div>
                                    :
                                    sortedModel.map((item, key) => {
                                        const status = resolveStatus(item);
                                        const monthly = monthlyOf(item) ?? globalState.prices?.subscriptionPerMonth;
                                        const wb = item.workspaceBilling;
                                        const renews = renewsAtOf(item);
                                        const isSwitching = switchingId === item.id;

                                        return (
                                            <div key={key} className="workspaces-row">

                                                <div className="ws-col-workspace d-flex align-items-center">
                                                    <Avatar avatarUrl={item.avatarUrl} fullName={item.companyName ?? item.fullName} width={42} />
                                                    <div className="ms-3 min-w-0 flex-grow-1">
                                                        <div className="text-dark fw-bolder fs-6 ws-name" title={item.fullName}>
                                                            {item.fullName}
                                                        </div>
                                                        {
                                                            item.username &&
                                                            <div className="d-flex align-items-center text-gray-500 fs-8 mt-1 ws-name" title={item.username}>
                                                                <Icon icon="at" className="me-1" size={11} />
                                                                {item.username}
                                                            </div>
                                                        }
                                                        {
                                                            item.companyName &&
                                                            <div className="d-flex align-items-center text-gray-500 fs-8 ws-name" title={item.companyName}>
                                                                <Icon icon="briefcase" className="me-1" size={11} />
                                                                {item.companyName}
                                                            </div>
                                                        }
                                                    </div>
                                                </div>

                                                <div className="ws-col-since fs-7">
                                                    {formatShortDate(item.createdAt)}
                                                </div>

                                                <div className="ws-col-amount fs-7 min-w-0">
                                                    {
                                                        wb?.planName &&
                                                        <div className="ws-name fs-8 fw-bolder text-gray-700"
                                                            title={[wb.planName, wb.billing, wb.customPrice ? "custom price" : ""].filter(Boolean).join(" · ")}>
                                                            {wb.planName}
                                                            {wb.billing === "annual" && <span className="text-gray-500 fw-semibold"> · annual</span>}
                                                            {wb.customPrice && <span className="text-gray-500 fw-semibold"> · custom</span>}
                                                        </div>
                                                    }
                                                    <div className="fw-bold">
                                                        {
                                                            monthly == null ? "-"
                                                                : Number(monthly) <= 1 ? <span className="text-gray-600 fs-8">Free Account</span>
                                                                    : <>${Number(monthly).toFixed(2)}<span className="text-gray-500 fs-9 fw-semibold">/mo</span></>
                                                        }
                                                    </div>
                                                    {
                                                        !!wb && wb.addonsMonthly > 0 &&
                                                        <div className="ws-name fs-9 text-gray-500" title={(wb.addons ?? []).join(", ")}>
                                                            incl. ${wb.addonsMonthly.toFixed(2)} add-ons
                                                        </div>
                                                    }
                                                </div>

                                                <div className="ws-col-status">
                                                    <span className={`badge ${status.className} fw-bold`}>
                                                        {status.label}
                                                    </span>
                                                    {
                                                        renews.date &&
                                                        <div className="fs-8 text-gray-600 mt-1">
                                                            {renews.active ? "Renews" : "Expires"}: {formatShortDate(new Date(renews.date))}
                                                        </div>
                                                    }
                                                </div>

                                                <div className="ws-col-action">
                                                    {
                                                        item.id === currentOwnerId ?
                                                            <span className="ws-current-badge">
                                                                <Icon icon="check-circle" type="solid" className="me-1" />
                                                                Current
                                                            </span>
                                                            :
                                                            <button type="button"
                                                                className="btn btn-sm btn-light-primary"
                                                                disabled={!!switchingId}
                                                                onClick={e => {
                                                                    e.preventDefault()
                                                                    switchTo(item)
                                                                }}>
                                                                {
                                                                    isSwitching ?
                                                                        <Indicator text="..." />
                                                                        :
                                                                        <>Switch</>
                                                                }
                                                            </button>
                                                    }
                                                </div>
                                            </div>
                                        );
                                    })
                        }
                    </div>
                </div>
            </div>
        </Modal>
    )
}
